import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import RoleSelectField from './RoleSelectField';
import useRequest from '../../hooks/useRequest';
import type { SalaryEmployee, SalaryRole } from '../../domain/salary/types';

interface Props {
    employees: SalaryEmployee[];
    roles: SalaryRole[];
    reload: () => Promise<void>;
}

interface SyncResponse {
    success: boolean;
    data: { inserted: number };
}

interface EmployeeForm {
    name: string;
    cnp: string;
    roleId: number;
    baseSalary: number;
    isActive: boolean;
    hireDate: string;
    prefersNightShifts: boolean;
}

const emptyForm: EmployeeForm = {
    name: '',
    cnp: '',
    roleId: 0,
    baseSalary: 0,
    isActive: true,
    hireDate: '',
    prefersNightShifts: false,
};

const EmployeesTab = ({ employees, roles, reload }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [form, setForm] = useState<EmployeeForm>({ ...emptyForm });
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [editing, setEditing] = useState<SalaryEmployee | null>(null);
    const [editForm, setEditForm] = useState<EmployeeForm>({ ...emptyForm });
    const [snack, setSnack] = useState<{
        open: boolean;
        severity: 'success' | 'error' | 'info';
        message: string;
    }>({ open: false, severity: 'success', message: '' });

    const showSnack = (severity: 'success' | 'error' | 'info', message: string) =>
        setSnack({ open: true, severity, message });

    const roleName = (roleId: number) => roles.find(r => r.id === roleId)?.name ?? '-';

    const normHoursForRole = (roleId: number) => {
        const norm = roles.find(r => r.id === roleId)?.config?.monthlyNormHours;
        return norm && norm > 0 ? norm : 168;
    };

    const derivedRate = (roleId: number, baseSalary: number) => {
        if (!baseSalary || baseSalary <= 0) return 0;
        return Math.round((baseSalary / normHoursForRole(roleId)) * 100) / 100;
    };

    const handleCreate = async () => {
        if (!form.name || !form.roleId) return;
        setSaving(true);
        try {
            await request({
                path: '/salary/employees',
                method: 'POST',
                params: {
                    name: form.name,
                    cnp: form.cnp || undefined,
                    roleId: form.roleId,
                    baseSalary: form.baseSalary,
                    isActive: form.isActive,
                    hireDate: form.hireDate || undefined,
                    prefersNightShifts: form.prefersNightShifts,
                },
            });
            setForm({ ...emptyForm });
            await reload();
            showSnack('success', t('salary.employees.created'));
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await request({ path: `/salary/employees/${id}`, method: 'DELETE' });
            await reload();
            showSnack('success', t('salary.employees.deleted'));
        } catch (error) {
            showSnack('error', (error as Error).message);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const response = await request<SyncResponse>({
                path: '/salary/employees/sync-users',
                method: 'POST',
            });
            await reload();
            showSnack(
                'success',
                t('salary.employees.syncResult', { count: response.data?.inserted ?? 0 }),
            );
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setSyncing(false);
        }
    };

    const openEdit = (employee: SalaryEmployee) => {
        setEditing(employee);
        setEditForm({
            name: employee.name,
            cnp: employee.cnp ?? '',
            roleId: employee.roleId,
            baseSalary: employee.baseSalary,
            isActive: employee.isActive,
            hireDate: employee.hireDate ?? '',
            prefersNightShifts: employee.prefersNightShifts,
        });
    };

    const closeEdit = () => setEditing(null);

    const handleSaveEdit = async () => {
        if (!editing) return;
        if (!editForm.name || !editForm.roleId) return;
        setSaving(true);
        try {
            await request({
                path: `/salary/employees/${editing.id}`,
                method: 'PUT',
                params: {
                    name: editForm.name,
                    cnp: editForm.cnp || null,
                    roleId: editForm.roleId,
                    baseSalary: editForm.baseSalary,
                    isActive: editForm.isActive,
                    hireDate: editForm.hireDate || null,
                    prefersNightShifts: editForm.prefersNightShifts,
                },
            });
            await reload();
            closeEdit();
            showSnack('success', t('salary.employees.updated'));
        } catch (error) {
            showSnack('error', (error as Error).message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('salary.employees.addTitle')}
                    <InfoHint title={t('salary.info.employees.section')} />
                </Typography>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                >
                    <TextField
                        size="small"
                        label={t('salary.employees.name')}
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                    <TextField
                        size="small"
                        label={t('salary.employees.cnp')}
                        value={form.cnp}
                        onChange={e => setForm({ ...form, cnp: e.target.value })}
                    />
                    <RoleSelectField
                        value={form.roleId}
                        onChange={roleId => setForm({ ...form, roleId })}
                        roles={roles}
                    />
                    <TextField
                        size="small"
                        type="number"
                        label={t('salary.employees.baseSalary')}
                        value={form.baseSalary}
                        onChange={e => setForm({ ...form, baseSalary: Number(e.target.value) })}
                        helperText={t('salary.employees.baseSalaryHelp', {
                            rate: derivedRate(form.roleId, form.baseSalary).toFixed(2),
                            hours: normHoursForRole(form.roleId).toFixed(0),
                        })}
                    />
                    <InfoHint title={t('salary.info.employees.baseSalary')} />
                    <TextField
                        size="small"
                        type="date"
                        label={t('salary.employees.hireDate')}
                        value={form.hireDate}
                        onChange={e => setForm({ ...form, hireDate: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <FormControlLabel
                        label={t('salary.employees.active')}
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={e => setForm({ ...form, isActive: e.target.checked })}
                            />
                        }
                    />
                    <InfoHint title={t('salary.info.employees.active')} />
                    <FormControlLabel
                        label={t('salary.employees.prefersNightShifts')}
                        control={
                            <Switch
                                checked={form.prefersNightShifts}
                                onChange={e =>
                                    setForm({ ...form, prefersNightShifts: e.target.checked })
                                }
                            />
                        }
                    />
                    <InfoHint title={t('salary.info.employees.prefersNightShifts')} />
                    <Button variant="contained" onClick={handleCreate} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : t('common.add')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing}
                    >
                        {syncing ? <CircularProgress size={20} /> : t('salary.employees.syncUsers')}
                    </Button>
                    <InfoHint title={t('salary.info.employees.syncUsers')} />
                </Stack>
            </Paper>

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('salary.employees.name')}</TableCell>
                            <TableCell>{t('salary.employees.cnp')}</TableCell>
                            <TableCell>{t('salary.employees.role')}</TableCell>
                            <TableCell align="right">{t('salary.employees.baseSalary')}</TableCell>
                            <TableCell align="right">{t('salary.employees.baseRate')}</TableCell>
                            <TableCell>{t('salary.employees.hireDate')}</TableCell>
                            <TableCell>{t('salary.employees.active')}</TableCell>
                            <TableCell>{t('salary.employees.prefersNightShifts')}</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Box py={2}>
                                        <Typography color="text.secondary">
                                            {t('salary.employees.noData')}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {employees.map(employee => (
                            <TableRow key={employee.id}>
                                <TableCell>{employee.name}</TableCell>
                                <TableCell>{employee.cnp ?? ''}</TableCell>
                                <TableCell>{roleName(employee.roleId)}</TableCell>
                                <TableCell align="right">
                                    {Math.round(employee.baseSalary)}
                                </TableCell>
                                <TableCell align="right">
                                    {employee.baseHourlyRate.toFixed(2)}
                                </TableCell>
                                <TableCell>{employee.hireDate ?? ''}</TableCell>
                                <TableCell>
                                    {employee.isActive
                                        ? t('salary.employees.active')
                                        : t('salary.employees.inactive')}
                                </TableCell>
                                <TableCell>{employee.prefersNightShifts ? '\u2713' : ''}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title={t('common.edit')}>
                                        <IconButton size="small" onClick={() => openEdit(employee)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('common.delete')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(employee.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={Boolean(editing)} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>{t('salary.employees.editTitle')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            size="small"
                            label={t('salary.employees.name')}
                            value={editForm.name}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label={t('salary.employees.cnp')}
                            value={editForm.cnp}
                            onChange={e => setEditForm({ ...editForm, cnp: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            select
                            label={t('salary.employees.role')}
                            value={editForm.roleId || ''}
                            onChange={e =>
                                setEditForm({ ...editForm, roleId: Number(e.target.value) })
                            }
                            fullWidth
                        >
                            {roles.map(role => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            type="number"
                            label={t('salary.employees.baseSalary')}
                            value={editForm.baseSalary}
                            onChange={e =>
                                setEditForm({
                                    ...editForm,
                                    baseSalary: Number(e.target.value),
                                })
                            }
                            helperText={t('salary.employees.baseSalaryHelp', {
                                rate: derivedRate(editForm.roleId, editForm.baseSalary).toFixed(2),
                                hours: normHoursForRole(editForm.roleId).toFixed(0),
                            })}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            type="date"
                            label={t('salary.employees.hireDate')}
                            value={editForm.hireDate}
                            onChange={e => setEditForm({ ...editForm, hireDate: e.target.value })}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                        />
                        <FormControlLabel
                            label={t('salary.employees.active')}
                            control={
                                <Switch
                                    checked={editForm.isActive}
                                    onChange={e =>
                                        setEditForm({ ...editForm, isActive: e.target.checked })
                                    }
                                />
                            }
                        />
                        <FormControlLabel
                            label={t('salary.employees.prefersNightShifts')}
                            control={
                                <Switch
                                    checked={editForm.prefersNightShifts}
                                    onChange={e =>
                                        setEditForm({
                                            ...editForm,
                                            prefersNightShifts: e.target.checked,
                                        })
                                    }
                                />
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack({ ...snack, open: false })}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

export default EmployeesTab;
